<script setup lang="ts">
import { useStudentMutations } from '@/composables/student/useStudentMutations';
import { usePermissions } from '@/composables/usePermissions';
import EmptyAvatar from '@/core/elements/EmptyAvatar.vue';
import FileUploadError from '@/core/elements/FileUploadError.vue';
import IconButton from '@/core/elements/IconButton.vue';
import TextInput from '@/core/elements/TextInput.vue';
import UserAvatarSelect from '@/core/elements/UserAvatarSelect.vue';
import { IOnboardingStudent } from '@/interfaces';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { createDraftStudent } from '@/utils/students.utils';
import { EFileUploadError } from 'common/enum/file.upload.enum';
import { EIcon } from 'common/enum/misc.enum';
import { MAX_UPLOAD_SIZE_AVATAR_PICTURE } from 'common/constants/file.upload.limits';
import { ALLOWED_FILE_TYPES_AVATAR_PICTURE } from 'common/constants/file.upload.types';
import { type TFileUploadError } from 'common/utils/file.utils';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';

const { unlinkStudentFromClass } = useStudentMutations();
const { canManageStudents } = usePermissions();
const { currentClass } = useCurrentClass();

const emit = defineEmits<{
  'update:modelValue': [value: IOnboardingStudent | null];
  enterPressed: [];
  tabPressed: [event: KeyboardEvent];
}>();

const emitEnter = (): void => {
  emit('enterPressed');
};

const emitTab = (event: KeyboardEvent): void => {
  emit('tabPressed', event);
};

const props = defineProps<{
  modelValue: IOnboardingStudent | null;
  disabled?: boolean;
  removable?: boolean;
  allowPictureUpload?: boolean;
}>();
const { modelValue, disabled, allowPictureUpload } = toRefs(props);
const firstNameInput = useTemplateRef('firstNameInput');
const lastNameInput = useTemplateRef('lastNameInput');
const fileUploadError = ref<TFileUploadError>(null);

const student = ref<IOnboardingStudent>(createDraftStudent());
const persistedAvatarPictureId = ref<number | null>(null);

const handleInputFirstName = (firstName: string): void => {
  student.value.firstName = firstName;
  emit('update:modelValue', student.value);
};

const handleInputLastName = (lastName: string): void => {
  student.value.lastName = lastName;
  emit('update:modelValue', student.value);
};

const handleRemoveStudent = async (): Promise<void> => {
  if (student.value.id && currentClass.value) {
    await unlinkStudentFromClass({ classId: currentClass.value.id, studentId: student.value.id });
  }
  fileUploadError.value = null;
  student.value = createDraftStudent();
  persistedAvatarPictureId.value = null;
  emit('update:modelValue', null);
};

const handleChangeAvatar = (avatar: string): void => {
  student.value.avatar = avatar;
  emit('update:modelValue', student.value);
};

const handleAvatarPictureSelected = (file: File): void => {
  fileUploadError.value = null;
  student.value.avatar = '';
  student.value.avatarPictureFile = file;
  student.value.avatarPictureId = null;
  student.value.clearAvatarPicture = false;
  emit('update:modelValue', student.value);
};

const handleAvatarPictureCleared = (): void => {
  student.value.avatarPictureFile = null;
  student.value.clearAvatarPicture =
    student.value.clearAvatarPicture === true || persistedAvatarPictureId.value != null;
  student.value.avatarPictureId = null;
  fileUploadError.value = null;
  emit('update:modelValue', student.value);
};

const isFilled = computed(() => student.value.firstName || student.value.lastName);
const isDisabled = computed(() => {
  if (canManageStudents.value) return disabled.value;
  return !!student.value.existing || disabled.value;
});
const canShowAvatarSelect = computed<boolean>(() =>
  Boolean(isFilled.value || (allowPictureUpload.value && !isDisabled.value)),
);

watch(
  modelValue,
  () => {
    if (!modelValue.value) return;

    if (modelValue.value.id !== student.value.id) {
      persistedAvatarPictureId.value = modelValue.value.avatarPictureId ?? null;
    }

    student.value = {
      id: modelValue.value.id,
      clientKey: modelValue.value.clientKey ?? crypto.randomUUID(),
      firstName: modelValue.value.firstName,
      lastName: modelValue.value.lastName,
      avatar: modelValue.value.avatar,
      avatarPictureFile: modelValue.value.avatarPictureFile ?? null,
      avatarUploadFailed: modelValue.value.avatarUploadFailed,
      avatarPictureId: modelValue.value.avatarPictureId ?? null,
      clearAvatarPicture: modelValue.value.clearAvatarPicture ?? false,
      existing: modelValue.value.existing,
    };
  },
  { immediate: true },
);

defineExpose({ firstNameInput, student });
</script>

<template>
  <div>
    <div class="flex gap-smd items-center">
      <EmptyAvatar v-if="!canShowAvatarSelect" />
      <UserAvatarSelect
        v-else
        :model-value="student"
        :disabled="isDisabled"
        :mode="'student'"
        :allow-picture-upload="allowPictureUpload"
        @avatar-picture-cleared="handleAvatarPictureCleared"
        @file-selected="handleAvatarPictureSelected"
        @file-size-exceeded="fileUploadError = EFileUploadError.SIZE"
        @file-type-invalid="fileUploadError = EFileUploadError.TYPE"
        @update:model-value="handleChangeAvatar"
      />
      <!-- TextInput blurs on Enter; the keydown bubbles to its root afterwards -->
      <TextInput
        ref="firstNameInput"
        class="min-w-0 flex-1"
        input-class="rounded-md"
        :model-value="student.firstName"
        :label="$t('general.firstName')"
        :disabled="isDisabled"
        @update:model-value="handleInputFirstName"
        @keydown.enter="lastNameInput?.focus()"
      />
      <TextInput
        ref="lastNameInput"
        class="min-w-0 flex-1"
        input-class="rounded-md"
        :model-value="student.lastName"
        :label="$t('general.lastName')"
        :disabled="isDisabled"
        @update:model-value="handleInputLastName"
        @keydown.enter="emitEnter"
        @keydown.tab.exact="emitTab"
      />
      <div v-if="removable" class="size-icon-button flex-shrink-0">
        <IconButton
          v-if="!isDisabled"
          type="button"
          :icon-name="EIcon.X"
          theme="secondary"
          @click="handleRemoveStudent()"
        />
      </div>
    </div>
    <FileUploadError
      :error="fileUploadError"
      :max-upload-size-bytes="MAX_UPLOAD_SIZE_AVATAR_PICTURE"
      :supported-file-types="ALLOWED_FILE_TYPES_AVATAR_PICTURE"
    />
  </div>
</template>
